import { MessageList } from '@mastra/core/agent';
import type { MastraMessageContentV2 } from '@mastra/core/agent';
import { ErrorCategory, ErrorDomain, MastraError } from '@mastra/core/error';
import type { MastraMessageV1, MastraDBMessage, StorageThreadType } from '@mastra/core/memory';
import {
  createStorageErrorId,
  ensureDate,
  isRunFenceConflictError,
  MemoryStorage,
  resolveRunFence,
  RUN_FENCING_TABLE_SCHEMAS,
  serializeDate,
  TABLE_MEMORY_RUN_FENCES,
  TABLE_MESSAGES,
  TABLE_THREADS,
  TABLE_RESOURCES,
  TABLE_SCHEMAS,
  normalizePerPage,
  calculatePagination,
  validateStorageMetadataFilter,
  executeRetentionPrune,
  resolveRetentionTargets,
  retentionCutoffMs,
} from '@mastra/core/storage';
import type {
  RunFence,
  StorageResourceType,
  StorageListMessagesInput,
  StorageListMessagesOutput,
  StorageMetadataFilter,
  StorageMetadataFilterValue,
  StorageListThreadsInput,
  StorageListThreadsOutput,
  TABLE_NAMES,
  PruneOptions,
  PruneResult,
  RetentionTablesDescriptor,
  TableRetentionPolicy,
} from '@mastra/core/storage';
import { D1DB, resolveD1Config } from '../../db';
import type { D1DomainConfig } from '../../db';
import { DB_NOW_MS, executeFenced, runFenceGuard } from '../../db/run-fencing';
import type { RunFenceCheck } from '../../db/run-fencing';
import { createSqlBuilder } from '../../sql-builder';
import { deserializeValue, isArrayOfRecords } from '../utils';

function addSqliteMetadataFilter(
  conditions: string[],
  params: unknown[],
  metadataFilter: StorageMetadataFilter | undefined,
): void {
  if (!metadataFilter) return;

  for (const [key, value] of Object.entries(metadataFilter)) {
    const path = `$.metadata.${key}`;
    conditions.push(`CASE WHEN json_valid(content) THEN json_type(content, ?) IS NOT NULL ELSE 0 END`);
    params.push(path);
    addSqliteMetadataValuePredicate(conditions, params, path, value);
  }
}

function addSqliteMetadataValuePredicate(
  conditions: string[],
  params: unknown[],
  path: string,
  value: StorageMetadataFilterValue,
): void {
  if (value === null) {
    conditions.push(`CASE WHEN json_valid(content) THEN json_type(content, ?) = 'null' ELSE 0 END`);
    params.push(path);
    return;
  }

  if (typeof value === 'string') {
    conditions.push(
      `CASE WHEN json_valid(content) THEN json_type(content, ?) = 'text' AND json_extract(content, ?) = ? ELSE 0 END`,
    );
    params.push(path, path, value);
    return;
  }

  if (typeof value === 'number') {
    conditions.push(
      `CASE WHEN json_valid(content) THEN json_type(content, ?) IN ('integer', 'real') AND json_extract(content, ?) = ? ELSE 0 END`,
    );
    params.push(path, path, value);
    return;
  }

  conditions.push(
    `CASE WHEN json_valid(content) THEN json_type(content, ?) = ? AND json_extract(content, ?) = ? ELSE 0 END`,
  );
  params.push(path, value ? 'true' : 'false', path, value ? 1 : 0);
}

export class MemoryStorageD1 extends MemoryStorage {
  /**
   * Tables `prune()` can clean up. Run fences anchor on `retiredAt` (epoch ms), which stays unset until the
   * execution holding the fence settles, so a running execution's fence is
   * never pruned.
   */
  static override readonly retentionTables: RetentionTablesDescriptor = {
    runFences: { table: TABLE_MEMORY_RUN_FENCES, column: 'retiredAt', indexed: false, anchorType: 'epoch-ms' },
  };

  override readonly supportsPartialThreadUpdate = true;
  #db: D1DB;

  constructor(config: D1DomainConfig) {
    super();
    this.#db = new D1DB(resolveD1Config(config));
  }

  async init(): Promise<void> {
    await this.#db.createTable({ tableName: TABLE_THREADS, schema: TABLE_SCHEMAS[TABLE_THREADS] });
    await this.#db.createTable({ tableName: TABLE_MESSAGES, schema: TABLE_SCHEMAS[TABLE_MESSAGES] });
    await this.#db.createTable({ tableName: TABLE_RESOURCES, schema: TABLE_SCHEMAS[TABLE_RESOURCES] });
    // Add resourceId column for backwards compatibility
    await this.#db.alterTable({
      tableName: TABLE_MESSAGES,
      schema: TABLE_SCHEMAS[TABLE_MESSAGES],
      ifNotExists: ['resourceId'],
    });
    await this.#db.createTable({
      tableName: TABLE_MEMORY_RUN_FENCES as TABLE_NAMES,
      schema: RUN_FENCING_TABLE_SCHEMAS[TABLE_MEMORY_RUN_FENCES],
    });
  }

  async prune(policies: Record<string, TableRetentionPolicy>, options?: PruneOptions): Promise<PruneResult[]> {
    const targets = resolveRetentionTargets({
      policies,
      descriptor: MemoryStorageD1.retentionTables,
      order: ['runFences'],
    });
    return executeRetentionPrune({
      domain: 'memory',
      targets,
      options,
      cutoffFor: (target, now) => retentionCutoffMs(target.policy, now),
      deleteBatch: (_target, cutoff, limit) =>
        this.#db.pruneRunRows({
          tableName: TABLE_MEMORY_RUN_FENCES as TABLE_NAMES,
          column: 'retiredAt',
          cutoff: Number(cutoff),
          limit,
        }),
    });
  }

  async dangerouslyClearAll(): Promise<void> {
    await this.#db.clearTable({ tableName: TABLE_MESSAGES });
    await this.#db.clearTable({ tableName: TABLE_THREADS });
    await this.#db.clearTable({ tableName: TABLE_RESOURCES });
    await this.#db.clearTable({ tableName: TABLE_MEMORY_RUN_FENCES as TABLE_NAMES });
  }

  override supportsRunFencing(): boolean {
    return true;
  }

  get #fencesTable(): string {
    return this.#db.getTableName(TABLE_MEMORY_RUN_FENCES as TABLE_NAMES);
  }

  override async raiseRunFence(fence: RunFence): Promise<boolean> {
    const table = this.#fencesTable;
    try {
      // One statement, so no other raise lands between judging the current fence and replacing it.
      // Re-raising the current fence writes the same row back and still counts as raised.
      // Every write un-retires the fence, so a reused run's fence isn't pruned mid-run.
      const rows = (await this.#db.executeQuery({
        sql: `INSERT INTO ${table} (runId, generation, ownerId) VALUES (?, ?, ?)
          ON CONFLICT(runId) DO UPDATE SET generation = excluded.generation, ownerId = excluded.ownerId, retiredAt = NULL
          WHERE excluded.generation > ${table}.generation
            OR (excluded.generation = ${table}.generation AND excluded.ownerId = ${table}.ownerId)
          RETURNING runId`,
        params: [fence.runId, fence.generation, fence.ownerId],
      })) as Record<string, unknown>[];
      return rows.length > 0;
    } catch (error) {
      throw new MastraError(
        {
          id: createStorageErrorId('CLOUDFLARE_D1', 'RAISE_RUN_FENCE', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: { runId: fence.runId },
        },
        error,
      );
    }
  }

  override async retireRunFence(fence: RunFence): Promise<boolean> {
    try {
      const rows = (await this.#db.executeQuery({
        sql: `UPDATE ${this.#fencesTable} SET retiredAt = ${DB_NOW_MS}
          WHERE runId = ? AND generation = ? AND ownerId = ?
          RETURNING runId`,
        params: [fence.runId, fence.generation, fence.ownerId],
      })) as Record<string, unknown>[];
      return rows.length > 0;
    } catch (error) {
      throw new MastraError(
        {
          id: createStorageErrorId('CLOUDFLARE_D1', 'RETIRE_RUN_FENCE', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: { runId: fence.runId },
        },
        error,
      );
    }
  }

  /** The check a write must pass: its own fence, otherwise the one in scope. */
  #runFenceCheck(fence: RunFence | undefined, operation: string): RunFenceCheck | undefined {
    const resolved = resolveRunFence(this, fence);
    return resolved && { claimsTable: this.#fencesTable, fence: resolved, operation };
  }

  async getResourceById({ resourceId }: { resourceId: string }): Promise<StorageResourceType | null> {
    const resource = await this.#db.load<StorageResourceType>({
      tableName: TABLE_RESOURCES,
      keys: { id: resourceId },
    });

    if (!resource) return null;

    try {
      return {
        ...resource,
        createdAt: ensureDate(resource.createdAt) as Date,
        updatedAt: ensureDate(resource.updatedAt) as Date,
        metadata:
          typeof resource.metadata === 'string'
            ? (JSON.parse(resource.metadata || '{}') as Record<string, any>)
            : resource.metadata,
      };
    } catch (error) {
      const mastraError = new MastraError(
        {
          id: createStorageErrorId('CLOUDFLARE_D1', 'GET_RESOURCE_BY_ID', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          text: `Error processing resource ${resourceId}: ${error instanceof Error ? error.message : String(error)}`,
          details: { resourceId },
        },
        error,
      );
      this.logger?.error(mastraError.toString());
      this.logger?.trackException(mastraError);
      return null;
    }
  }

  async saveResource({ resource }: { resource: StorageResourceType }): Promise<StorageResourceType> {
    return this.#insertResource(resource, undefined);
  }

  async #insertResource(resource: StorageResourceType, check: RunFenceCheck | undefined): Promise<StorageResourceType> {
    const fullTableName = this.#db.getTableName(TABLE_RESOURCES);

    // Prepare the record for SQL insertion
    const resourceToSave = {
      id: resource.id,
      workingMemory: resource.workingMemory,
      metadata: resource.metadata ? JSON.stringify(resource.metadata) : null,
      createdAt: resource.createdAt,
      updatedAt: resource.updatedAt,
    };

    // Process record for SQL insertion
    const processedRecord = await this.#db.processRecord(resourceToSave);

    const columns = Object.keys(processedRecord);
    const values = Object.values(processedRecord);

    // Specify which columns to update on conflict (all except id)
    const updateMap: Record<string, string> = {
      workingMemory: 'excluded.workingMemory',
      metadata: 'excluded.metadata',
      createdAt: 'excluded.createdAt',
      updatedAt: 'excluded.updatedAt',
    };

    // Use the new insert method with ON CONFLICT
    const query = createSqlBuilder().insert(fullTableName, columns, values, ['id'], updateMap, runFenceGuard(check));

    try {
      await executeFenced(this.#db, check, query.build());
      return resource;
    } catch (error) {
      if (isRunFenceConflictError(error)) throw error;
      throw new MastraError(
        {
          id: createStorageErrorId('CLOUDFLARE_D1', 'SAVE_RESOURCE', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          text: `Failed to save resource to ${fullTableName}: ${error instanceof Error ? error.message : String(error)}`,
          details: { resourceId: resource.id },
        },
        error,
      );
    }
  }

  async updateResource({
    resourceId,
    workingMemory,
    metadata,
    fence,
  }: {
    resourceId: string;
    workingMemory?: string;
    metadata?: Record<string, unknown>;
    fence?: RunFence;
  }): Promise<StorageResourceType> {
    const check = this.#runFenceCheck(fence, 'updateResource');
    const existingResource = await this.getResourceById({ resourceId });

    if (!existingResource) {
      // Create new resource if it doesn't exist
      const newResource: StorageResourceType = {
        id: resourceId,
        workingMemory,
        metadata: metadata || {},
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      return this.#insertResource(newResource, check);
    }

    const updatedAt = new Date();
    const updatedResource = {
      ...existingResource,
      workingMemory: workingMemory !== undefined ? workingMemory : existingResource.workingMemory,
      metadata: {
        ...existingResource.metadata,
        ...metadata,
      },
      updatedAt,
    };

    const fullTableName = this.#db.getTableName(TABLE_RESOURCES);

    const columns = ['workingMemory', 'metadata', 'updatedAt'];
    const values = [updatedResource.workingMemory, JSON.stringify(updatedResource.metadata), updatedAt.toISOString()];

    const query = createSqlBuilder().update(fullTableName, columns, values).where('id = ?', resourceId);
    const guard = runFenceGuard(check);
    if (guard) query.andWhere(guard.sql, ...guard.params);

    try {
      await executeFenced(this.#db, check, query.build());
      return updatedResource;
    } catch (error) {
      if (isRunFenceConflictError(error)) throw error;
      throw new MastraError(
        {
          id: createStorageErrorId('CLOUDFLARE_D1', 'UPDATE_RESOURCE', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          text: `Failed to update resource ${resourceId}: ${error instanceof Error ? error.message : String(error)}`,
          details: { resourceId },
        },
        error,
      );
    }
  }

  async getThreadById({
    threadId,
    resourceId,
  }: {
    threadId: string;
    resourceId?: string;
  }): Promise<StorageThreadType | null> {
    const thread = await this.#db.load<StorageThreadType>({
      tableName: TABLE_THREADS,
      keys: { id: threadId },
    });

    if (!thread || (resourceId !== undefined && thread.resourceId !== resourceId)) return null;

    try {
      return {
        ...thread,
        createdAt: ensureDate(thread.createdAt) as Date,
        updatedAt: ensureDate(thread.updatedAt) as Date,
        metadata:
          typeof thread.metadata === 'string'
            ? (JSON.parse(thread.metadata || '{}') as Record<string, any>)
            : thread.metadata || {},
      };
    } catch (error) {
      const mastraError = new MastraError(
        {
          id: createStorageErrorId('CLOUDFLARE_D1', 'GET_THREAD_BY_ID', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          text: `Error processing thread ${threadId}: ${error instanceof Error ? error.message : String(error)}`,
          details: { threadId },
        },
        error,
      );
      this.logger?.error(mastraError.toString());
      this.logger?.trackException(mastraError);
      return null;
    }
  }

  public async listThreads(args: StorageListThreadsInput): Promise<StorageListThreadsOutput> {
    const { page = 0, perPage: perPageInput, orderBy, filter } = args;

    try {
      // Validate pagination input before normalization
      // This ensures page === 0 when perPageInput === false
      this.validatePaginationInput(page, perPageInput ?? 100);
    } catch (error) {
      throw new MastraError(
        {
          id: createStorageErrorId('CLOUDFLARE_D1', 'LIST_THREADS', 'INVALID_PAGE'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.USER,
          details: { page, ...(perPageInput !== undefined && { perPage: perPageInput }) },
        },
        error instanceof Error ? error : new Error('Invalid pagination parameters'),
      );
    }

    const perPage = normalizePerPage(perPageInput, 100);

    // Validate metadata keys to prevent SQL injection
    try {
      this.validateMetadataKeys(filter?.metadata);
    } catch (error) {
      throw new MastraError(
        {
          id: createStorageErrorId('CLOUDFLARE_D1', 'LIST_THREADS', 'INVALID_METADATA_KEY'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.USER,
          details: { metadataKeys: filter?.metadata ? Object.keys(filter.metadata).join(', ') : '' },
        },
        error instanceof Error ? error : new Error('Invalid metadata key'),
      );
    }

    const { offset, perPage: perPageForResponse } = calculatePagination(page, perPageInput, perPage);
    const { field, direction } = this.parseOrderBy(orderBy);
    const fullTableName = this.#db.getTableName(TABLE_THREADS);

    const mapRowToStorageThreadType = (row: Record<string, any>): StorageThreadType => ({
      ...(row as StorageThreadType),
      createdAt: ensureDate(row.createdAt) as Date,
      updatedAt: ensureDate(row.updatedAt) as Date,
      metadata:
        typeof row.metadata === 'string'
          ? (JSON.parse(row.metadata || '{}') as Record<string, any>)
          : row.metadata || {},
    });

    try {
      let countQuery = createSqlBuilder().count().from(fullTableName);
      let selectQuery = createSqlBuilder().select('*').from(fullTableName);

      // Add resourceId filter if provided
      if (filter?.resourceId) {
        countQuery = countQuery.whereAnd('resourceId = ?', filter.resourceId);
        selectQuery = selectQuery.whereAnd('resourceId = ?', filter.resourceId);
      }

      // Add metadata filters if provided (AND logic)
      // Keys are validated above to prevent SQL injection
      if (filter?.metadata && Object.keys(filter.metadata).length > 0) {
        for (const [key, value] of Object.entries(filter.metadata)) {
          // Validate filter value type - only allow scalar types
          if (value !== null && typeof value === 'object') {
            throw new MastraError(
              {
                id: createStorageErrorId('CLOUDFLARE_D1', 'LIST_THREADS', 'INVALID_METADATA_VALUE'),
                domain: ErrorDomain.STORAGE,
                category: ErrorCategory.USER,
                text: `Metadata filter value for key "${key}" must be a scalar type (string, number, boolean, or null), got ${Array.isArray(value) ? 'array' : 'object'}`,
                details: { key, valueType: Array.isArray(value) ? 'array' : 'object' },
              },
              new Error('Invalid metadata filter value type'),
            );
          }

          // Handle null values specially: json_extract returns NULL for null values,
          // and NULL = NULL evaluates to NULL (not true) in SQL
          if (value === null) {
            const condition = `json_extract(metadata, '$.${key}') IS NULL`;
            countQuery = countQuery.whereAnd(condition);
            selectQuery = selectQuery.whereAnd(condition);
          } else {
            // json_extract returns the raw value (unquoted for strings, native for numbers/booleans)
            // Pass native values directly so SQLite compares types correctly
            const condition = `json_extract(metadata, '$.${key}') = ?`;
            // Type assertion is safe here because we've validated above that value is a scalar type
            const filterValue = value as string | number | boolean;
            countQuery = countQuery.whereAnd(condition, filterValue);
            selectQuery = selectQuery.whereAnd(condition, filterValue);
          }
        }
      }

      const countResult = (await this.#db.executeQuery(countQuery.build())) as {
        count: number;
      }[];
      const total = Number(countResult?.[0]?.count ?? 0);

      if (total === 0) {
        return {
          threads: [],
          total: 0,
          page,
          perPage: perPageForResponse,
          hasMore: false,
        };
      }

      const limitValue = perPageInput === false ? total : perPage;
      selectQuery = selectQuery.orderBy(field, direction).limit(limitValue).offset(offset);

      const results = (await this.#db.executeQuery(selectQuery.build())) as Record<string, any>[];
      const threads = results.map(mapRowToStorageThreadType);

      return {
        threads,
        total,
        page,
        perPage: perPageForResponse,
        hasMore: perPageInput === false ? false : offset + perPage < total,
      };
    } catch (error) {
      // Re-throw USER errors (validation errors) directly so callers get proper 400 responses
      if (error instanceof MastraError && error.category === ErrorCategory.USER) {
        throw error;
      }
      const mastraError = new MastraError(
        {
          id: createStorageErrorId('CLOUDFLARE_D1', 'LIST_THREADS', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          text: `Error listing threads: ${error instanceof Error ? error.message : String(error)}`,
          details: {
            ...(filter?.resourceId && { resourceId: filter.resourceId }),
            hasMetadataFilter: !!filter?.metadata,
          },
        },
        error,
      );
      this.logger?.error(mastraError.toString());
      this.logger?.trackException(mastraError);
      throw mastraError;
    }
  }

  async saveThread({ thread, fence }: { thread: StorageThreadType; fence?: RunFence }): Promise<StorageThreadType> {
    const fullTableName = this.#db.getTableName(TABLE_THREADS);
    const check = this.#runFenceCheck(fence, 'saveThread');

    // Prepare the record for SQL insertion
    const threadToSave = {
      id: thread.id,
      resourceId: thread.resourceId,
      title: thread.title,
      metadata: thread.metadata ? JSON.stringify(thread.metadata) : null,
      createdAt: thread.createdAt.toISOString(),
      updatedAt: thread.updatedAt.toISOString(),
    };

    // Process record for SQL insertion
    const processedRecord = await this.#db.processRecord(threadToSave);

    const columns = Object.keys(processedRecord);
    const values = Object.values(processedRecord);

    // Specify which columns to update on conflict (all except id)
    const updateMap: Record<string, string> = {
      resourceId: 'excluded.resourceId',
      title: 'excluded.title',
      metadata: 'excluded.metadata',
      createdAt: 'excluded.createdAt',
      updatedAt: 'excluded.updatedAt',
    };

    // Use the new insert method with ON CONFLICT
    const query = createSqlBuilder().insert(fullTableName, columns, values, ['id'], updateMap, runFenceGuard(check));

    try {
      await executeFenced(this.#db, check, query.build());
      return thread;
    } catch (error) {
      if (isRunFenceConflictError(error)) throw error;
      throw new MastraError(
        {
          id: createStorageErrorId('CLOUDFLARE_D1', 'SAVE_THREAD', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          text: `Failed to save thread to ${fullTableName}: ${error instanceof Error ? error.message : String(error)}`,
          details: { threadId: thread.id },
        },
        error,
      );
    }
  }

  async updateThread({
    id,
    title,
    metadata,
    fence,
  }: {
    id: string;
    title?: string;
    metadata?: Record<string, unknown>;
    fence?: RunFence;
  }): Promise<StorageThreadType> {
    const check = this.#runFenceCheck(fence, 'updateThread');
    const thread = await this.getThreadById({ threadId: id });
    try {
      if (!thread) {
        throw new Error(`Thread ${id} not found`);
      }
      const fullTableName = this.#db.getTableName(TABLE_THREADS);

      const mergedMetadata = {
        ...(typeof thread.metadata === 'string' ? JSON.parse(thread.metadata) : thread.metadata),
        ...(metadata as Record<string, any>),
      };

      const updatedAt = new Date();
      const columns = ['title', 'metadata', 'updatedAt'];
      const values = [title ?? thread.title, JSON.stringify(mergedMetadata), updatedAt.toISOString()];

      const query = createSqlBuilder().update(fullTableName, columns, values).where('id = ?', id);
      const guard = runFenceGuard(check);
      if (guard) query.andWhere(guard.sql, ...guard.params);

      await executeFenced(this.#db, check, query.build());

      return {
        ...thread,
        title: title ?? thread.title,
        metadata: {
          ...(typeof thread.metadata === 'string' ? JSON.parse(thread.metadata) : thread.metadata),
          ...(metadata as Record<string, any>),
        },
        updatedAt,
      };
    } catch (error) {
      if (isRunFenceConflictError(error)) throw error;
      throw new MastraError(
        {
          id: createStorageErrorId('CLOUDFLARE_D1', 'UPDATE_THREAD', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          text: `Failed to update thread ${id}: ${error instanceof Error ? error.message : String(error)}`,
          details: { threadId: id },
        },
        error,
      );
    }
  }

  async deleteThread({ threadId }: { threadId: string }): Promise<void> {
    const fullTableName = this.#db.getTableName(TABLE_THREADS);

    try {
      // Delete the thread
      const deleteThreadQuery = createSqlBuilder().delete(fullTableName).where('id = ?', threadId);

      const { sql: threadSql, params: threadParams } = deleteThreadQuery.build();
      await this.#db.executeQuery({ sql: threadSql, params: threadParams });

      // Also delete associated messages
      const messagesTableName = this.#db.getTableName(TABLE_MESSAGES);
      const deleteMessagesQuery = createSqlBuilder().delete(messagesTableName).where('thread_id = ?', threadId);

      const { sql: messagesSql, params: messagesParams } = deleteMessagesQuery.build();
      await this.#db.executeQuery({ sql: messagesSql, params: messagesParams });
    } catch (error) {
      throw new MastraError(
        {
          id: createStorageErrorId('CLOUDFLARE_D1', 'DELETE_THREAD', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          text: `Failed to delete thread ${threadId}: ${error instanceof Error ? error.message : String(error)}`,
          details: { threadId },
        },
        error,
      );
    }
  }

  async saveMessages(args: {
    messages: MastraDBMessage[];
    fence?: RunFence;
  }): Promise<{ messages: MastraDBMessage[] }> {
    const { messages } = args;
    if (messages.length === 0) return { messages: [] };
    const check = this.#runFenceCheck(args.fence, 'saveMessages');

    try {
      const now = new Date();
      const threadId = messages[0]?.threadId;

      // Validate all messages before insert
      for (const [i, message] of messages.entries()) {
        if (!message.id) throw new Error(`Message at index ${i} missing id`);
        if (!message.threadId) {
          throw new Error(`Message at index ${i} missing threadId`);
        }
        if (!message.content) {
          throw new Error(`Message at index ${i} missing content`);
        }
        if (!message.role) {
          throw new Error(`Message at index ${i} missing role`);
        }
        if (!message.resourceId) {
          throw new Error(`Message at index ${i} missing resourceId`);
        }
        const thread = await this.getThreadById({ threadId: message.threadId });
        if (!thread) {
          throw new Error(`Thread ${message.threadId} not found`);
        }
      }

      // Prepare all messages for insertion (set timestamps, thread_id, etc.)
      const messagesToInsert = messages.map(message => {
        const createdAt = message.createdAt ? new Date(message.createdAt) : now;
        return {
          id: message.id,
          thread_id: message.threadId,
          content: typeof message.content === 'string' ? message.content : JSON.stringify(message.content),
          createdAt: createdAt.toISOString(),
          role: message.role,
          type: message.type || 'v2',
          resourceId: message.resourceId,
        };
      });

      // Insert messages, then update thread's updatedAt. In order: a fenced save that lost its run
      // must stop at its first refused statement, before anything after it lands.
      await this.#db.batchUpsert({
        tableName: TABLE_MESSAGES,
        records: messagesToInsert,
        check,
      });
      const guard = runFenceGuard(check);
      await executeFenced(this.#db, check, {
        sql: `UPDATE ${this.#db.getTableName(TABLE_THREADS)} SET updatedAt = ? WHERE id = ?${guard ? ` AND ${guard.sql}` : ''}`,
        params: [now.toISOString(), threadId, ...(guard?.params ?? [])],
      });

      this.logger.debug(`Saved ${messages.length} messages`);
      const list = new MessageList().add(messages, 'memory');
      return { messages: list.get.all.db() };
    } catch (error) {
      if (isRunFenceConflictError(error)) throw error;
      throw new MastraError(
        {
          id: createStorageErrorId('CLOUDFLARE_D1', 'SAVE_MESSAGES', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          text: `Failed to save messages: ${error instanceof Error ? error.message : String(error)}`,
        },
        error,
      );
    }
  }

  private _sortMessages(messages: MastraDBMessage[], field: string, direction: string): MastraDBMessage[] {
    return messages.sort((a, b) => {
      const isDateField = field === 'createdAt' || field === 'updatedAt';
      const aValue = isDateField ? new Date((a as any)[field]).getTime() : (a as any)[field];
      const bValue = isDateField ? new Date((b as any)[field]).getTime() : (b as any)[field];

      if (aValue === bValue) {
        return a.id.localeCompare(b.id);
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return direction === 'ASC' ? aValue - bValue : bValue - aValue;
      }
      return direction === 'ASC'
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }

  /**
   * Fetches the messages named by `include` together with their surrounding context.
   *
   * @param include - Message ids to pin, each with an optional before/after window.
   * @param resourceId - When set, restricts both the pinned messages and their context
   * to that resource so an id from another resource returns nothing.
   */
  private async _getIncludedMessages(include: StorageListMessagesInput['include'], resourceId?: string) {
    if (!include || include.length === 0) return null;

    const tableName = this.#db.getTableName(TABLE_MESSAGES);
    const resourceCondition = resourceId ? ` AND resourceId = ?` : '';

    // Phase 1: Batch-fetch metadata for all target messages in a single query.
    // This eliminates the correlated subselects that previously ran per-subquery.
    const targetIds = include.map(inc => inc.id).filter(Boolean);
    if (targetIds.length === 0) return null;

    const idPlaceholders = targetIds.map(() => '?').join(', ');
    const targetResult = await this.#db.executeQuery({
      sql: `SELECT id, thread_id, createdAt FROM ${tableName} WHERE id IN (${idPlaceholders})${resourceCondition}`,
      params: resourceId ? [...targetIds, resourceId] : targetIds,
    });

    if (!Array.isArray(targetResult) || targetResult.length === 0) return null;

    const targetMap = new Map(
      targetResult.map((r: any) => [r.id as string, { threadId: r.thread_id as string, createdAt: r.createdAt }]),
    );

    // Phase 2: Build cursor-based subqueries and execute in batches to stay under
    // D1's compound SELECT limit. D1 sets SQLITE_LIMIT_COMPOUND_SELECT well below
    // SQLite's default of 500 — empirically it fails at 10 terms, so we use a
    // conservative batch size of 5 terms.
    const MAX_UNION_TERMS = 5;
    const unionQueries: string[] = [];
    const unionParams: any[] = [];
    const allMessages: Record<string, any>[] = [];

    const flushBatch = async () => {
      if (unionQueries.length === 0) return;
      const sql =
        unionQueries.length === 1
          ? unionQueries[0]!
          : `${unionQueries.join(' UNION ALL ')} ORDER BY createdAt ASC, id ASC`;
      const result = await this.#db.executeQuery({ sql, params: unionParams });
      if (Array.isArray(result)) {
        allMessages.push(...result);
      }
      unionQueries.length = 0;
      unionParams.length = 0;
    };

    for (const inc of include) {
      const { id, withPreviousMessages = 0, withNextMessages = 0 } = inc;
      const target = targetMap.get(id);
      if (!target) continue;

      // Check if adding this target's subqueries would exceed the batch limit
      const termsNeeded = withNextMessages > 0 ? 2 : 1;
      if (unionQueries.length + termsNeeded > MAX_UNION_TERMS) {
        await flushBatch();
      }

      unionQueries.push(`SELECT * FROM (
        SELECT id, content, role, type, createdAt, thread_id AS threadId, resourceId
        FROM ${tableName}
        WHERE thread_id = ?
          AND createdAt <= ?${resourceCondition}
        ORDER BY createdAt DESC, id DESC
        LIMIT ?
      )`);
      unionParams.push(target.threadId, target.createdAt);
      if (resourceId) unionParams.push(resourceId);
      unionParams.push(withPreviousMessages + 1);

      if (withNextMessages > 0) {
        unionQueries.push(`SELECT * FROM (
          SELECT id, content, role, type, createdAt, thread_id AS threadId, resourceId
          FROM ${tableName}
          WHERE thread_id = ?
            AND createdAt > ?${resourceCondition}
          ORDER BY createdAt ASC, id ASC
          LIMIT ?
        )`);
        unionParams.push(target.threadId, target.createdAt);
        if (resourceId) unionParams.push(resourceId);
        unionParams.push(withNextMessages);
      }
    }

    await flushBatch();

    // Parse message content and deduplicate
    const seen = new Set<string>();
    const processedMessages = allMessages
      .filter((message: Record<string, any>) => {
        const id = message.id as string;
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      })
      .map((message: Record<string, any>) => {
        const processedMsg: Record<string, any> = {};
        for (const [key, value] of Object.entries(message)) {
          if (key === `type` && value === `v2`) continue;
          processedMsg[key] = deserializeValue(value);
        }
        return processedMsg;
      });

    return processedMessages;
  }

  public async listMessagesById({ messageIds }: { messageIds: string[] }): Promise<{ messages: MastraDBMessage[] }> {
    if (messageIds.length === 0) return { messages: [] };
    const fullTableName = this.#db.getTableName(TABLE_MESSAGES);
    const messages: any[] = [];

    try {
      const query = createSqlBuilder()
        .select(['id', 'content', 'role', 'type', 'createdAt', 'thread_id AS threadId', 'resourceId'])
        .from(fullTableName)
        .where(`id in (${messageIds.map(() => '?').join(',')})`, ...messageIds);

      query.orderBy('createdAt', 'DESC');

      const { sql, params } = query.build();

      const result = await this.#db.executeQuery({ sql, params });

      if (Array.isArray(result)) messages.push(...result);

      // Parse message content
      const processedMessages = messages.map(message => {
        const processedMsg: Record<string, any> = {};

        for (const [key, value] of Object.entries(message)) {
          if (key === `type` && value === `v2`) continue;
          processedMsg[key] = deserializeValue(value);
        }

        return processedMsg;
      });
      this.logger.debug(`Retrieved ${messages.length} messages`);
      const list = new MessageList().add(processedMessages as MastraMessageV1[] | MastraDBMessage[], 'memory');
      return { messages: list.get.all.db() };
    } catch (error) {
      const mastraError = new MastraError(
        {
          id: createStorageErrorId('CLOUDFLARE_D1', 'LIST_MESSAGES_BY_ID', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          text: `Failed to retrieve messages by ID: ${error instanceof Error ? error.message : String(error)}`,
          details: { messageIds: JSON.stringify(messageIds) },
        },
        error,
      );
      this.logger?.error(mastraError.toString());
      this.logger?.trackException(mastraError);
      throw mastraError;
    }
  }

  public async listMessages(args: StorageListMessagesInput): Promise<StorageListMessagesOutput> {
    const { threadId, resourceId, include, filter, perPage: perPageInput, page = 0, orderBy } = args;

    // Normalize threadId to array
    const threadIds = Array.isArray(threadId) ? threadId : [threadId];

    if (threadIds.length === 0 || threadIds.some(id => !id.trim())) {
      throw new MastraError(
        {
          id: createStorageErrorId('CLOUDFLARE_D1', 'LIST_MESSAGES', 'INVALID_THREAD_ID'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: { threadId: Array.isArray(threadId) ? threadId.join(',') : threadId },
        },
        new Error('threadId must be a non-empty string or array of non-empty strings'),
      );
    }

    if (page < 0) {
      throw new MastraError(
        {
          id: createStorageErrorId('CLOUDFLARE_D1', 'LIST_MESSAGES', 'INVALID_PAGE'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.USER,
          details: { page },
        },
        new Error('page must be >= 0'),
      );
    }

    const perPage = normalizePerPage(perPageInput, 40);
    const { offset, perPage: perPageForResponse } = calculatePagination(page, perPageInput, perPage);
    const metadataFilter = validateStorageMetadataFilter(filter?.metadata);

    try {
      const fullTableName = this.#db.getTableName(TABLE_MESSAGES);

      // Step 1: Get paginated messages from the thread first (without excluding included ones)
      const threadPlaceholders = threadIds.map(() => '?').join(', ');
      let query = `
        SELECT id, content, role, type, createdAt, thread_id AS threadId, resourceId
        FROM ${fullTableName}
        WHERE thread_id IN (${threadPlaceholders})
      `;
      const queryParams: any[] = [...threadIds];

      if (resourceId) {
        query += ` AND resourceId = ?`;
        queryParams.push(resourceId);
      }

      const dateRange = filter?.dateRange;
      if (dateRange?.start) {
        const startDate =
          dateRange.start instanceof Date ? serializeDate(dateRange.start) : serializeDate(new Date(dateRange.start));
        const startOp = dateRange.startExclusive ? '>' : '>=';
        query += ` AND createdAt ${startOp} ?`;
        queryParams.push(startDate);
      }

      if (dateRange?.end) {
        const endDate =
          dateRange.end instanceof Date ? serializeDate(dateRange.end) : serializeDate(new Date(dateRange.end));
        const endOp = dateRange.endExclusive ? '<' : '<=';
        query += ` AND createdAt ${endOp} ?`;
        queryParams.push(endDate);
      }

      const metadataConditions: string[] = [];
      addSqliteMetadataFilter(metadataConditions, queryParams, metadataFilter);
      if (metadataConditions.length > 0) {
        query += ` AND ${metadataConditions.join(' AND ')}`;
      }

      // Build ORDER BY clause
      const { field, direction } = this.parseOrderBy(orderBy, 'ASC');

      // When perPage is 0 with no includes, there's nothing to return.
      if (perPage === 0 && (!include || include.length === 0)) {
        return { messages: [], total: 0, page, perPage: perPageForResponse, hasMore: false };
      }

      // When perPage is 0 and we have include targets, skip COUNT and data queries.
      // This is the semantic recall path where we only need the included messages.
      if (perPage === 0 && include && include.length > 0) {
        const includeResult = await this._getIncludedMessages(include, resourceId);
        if (!Array.isArray(includeResult) || includeResult.length === 0) {
          return { messages: [], total: 0, page, perPage: perPageForResponse, hasMore: false };
        }
        const list = new MessageList().add(includeResult as MastraMessageV1[] | MastraDBMessage[], 'memory');
        return {
          messages: this._sortMessages(list.get.all.db(), field, direction),
          total: 0,
          page,
          perPage: perPageForResponse,
          hasMore: false,
        };
      }

      query += ` ORDER BY "${field}" ${direction}`;

      // Apply pagination
      if (perPage !== Number.MAX_SAFE_INTEGER) {
        query += ` LIMIT ? OFFSET ?`;
        queryParams.push(perPage, offset);
      }

      const results = await this.#db.executeQuery({ sql: query, params: queryParams });

      // Parse message content
      const paginatedMessages = (isArrayOfRecords(results) ? results : []).map((message: Record<string, any>) => {
        const processedMsg: Record<string, any> = {};
        for (const [key, value] of Object.entries(message)) {
          if (key === `type` && value === `v2`) continue;
          processedMsg[key] = deserializeValue(value);
        }
        return processedMsg;
      });

      const paginatedCount = paginatedMessages.length;

      // Get total count
      let countQuery = `SELECT count() as count FROM ${fullTableName} WHERE thread_id IN (${threadPlaceholders})`;
      const countParams: any[] = [...threadIds];

      if (resourceId) {
        countQuery += ` AND resourceId = ?`;
        countParams.push(resourceId);
      }

      if (dateRange?.start) {
        const startDate =
          dateRange.start instanceof Date ? serializeDate(dateRange.start) : serializeDate(new Date(dateRange.start));
        const startOp = dateRange.startExclusive ? '>' : '>=';
        countQuery += ` AND createdAt ${startOp} ?`;
        countParams.push(startDate);
      }

      if (dateRange?.end) {
        const endDate =
          dateRange.end instanceof Date ? serializeDate(dateRange.end) : serializeDate(new Date(dateRange.end));
        const endOp = dateRange.endExclusive ? '<' : '<=';
        countQuery += ` AND createdAt ${endOp} ?`;
        countParams.push(endDate);
      }

      const countMetadataConditions: string[] = [];
      addSqliteMetadataFilter(countMetadataConditions, countParams, metadataFilter);
      if (countMetadataConditions.length > 0) {
        countQuery += ` AND ${countMetadataConditions.join(' AND ')}`;
      }

      const countResult = (await this.#db.executeQuery({ sql: countQuery, params: countParams })) as {
        count: number;
      }[];
      const total = Number(countResult[0]?.count ?? 0);

      // Only return early if there are no messages AND no includes to process
      if (total === 0 && paginatedCount === 0 && (!include || include.length === 0)) {
        return {
          messages: [],
          total: 0,
          page,
          perPage: perPageForResponse,
          hasMore: false,
        };
      }

      // Step 2: Add included messages with context (if any), excluding duplicates
      const messageIds = new Set(paginatedMessages.map((m: Record<string, any>) => m.id as string));
      let includeMessages: MastraDBMessage[] = [];

      if (include && include.length > 0) {
        // Use the existing _getIncludedMessages helper, but adapt it for listMessages format
        const includeResult = (await this._getIncludedMessages(include, resourceId)) as MastraDBMessage[];
        if (Array.isArray(includeResult)) {
          includeMessages = includeResult;

          // Deduplicate: only add messages that aren't already in the paginated results
          for (const includeMsg of includeMessages) {
            if (!messageIds.has(includeMsg.id)) {
              paginatedMessages.push(includeMsg);
              messageIds.add(includeMsg.id);
            }
          }
        }
      }

      // Use MessageList for proper deduplication and format conversion to V2
      const list = new MessageList().add(paginatedMessages as MastraMessageV1[] | MastraDBMessage[], 'memory');
      const finalMessages = this._sortMessages(list.get.all.db(), field, direction);

      // Calculate hasMore based on pagination window
      // If all thread messages have been returned (through pagination or include), hasMore = false
      // Otherwise, check if there are more pages in the pagination window
      const threadIdSet = new Set(threadIds);
      const returnedThreadMessageIds = new Set(
        finalMessages.filter(m => m.threadId && threadIdSet.has(m.threadId)).map(m => m.id),
      );
      const allThreadMessagesReturned = returnedThreadMessageIds.size >= total;
      const hasMore = metadataFilter
        ? perPageInput !== false && offset + paginatedCount < total
        : perPageInput !== false && !allThreadMessagesReturned && offset + perPage < total;

      return {
        messages: finalMessages,
        total,
        page,
        perPage: perPageForResponse,
        hasMore,
      };
    } catch (error: any) {
      // Re-throw USER errors (validation errors) directly so callers get proper 400 responses
      if (error instanceof MastraError && error.category === ErrorCategory.USER) {
        throw error;
      }
      const mastraError = new MastraError(
        {
          id: createStorageErrorId('CLOUDFLARE_D1', 'LIST_MESSAGES', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          text: `Failed to list messages for thread ${Array.isArray(threadId) ? threadId.join(',') : threadId}: ${
            error instanceof Error ? error.message : String(error)
          }`,
          details: {
            threadId: Array.isArray(threadId) ? threadId.join(',') : threadId,
            resourceId: resourceId ?? '',
          },
        },
        error,
      );
      this.logger?.error?.(mastraError.toString());
      this.logger?.trackException?.(mastraError);
      throw mastraError;
    }
  }

  async updateMessages(args: {
    messages: Partial<Omit<MastraDBMessage, 'createdAt'>> &
      {
        id: string;
        content?: {
          metadata?: MastraMessageContentV2['metadata'];
          content?: MastraMessageContentV2['content'];
        };
      }[];
    fence?: RunFence;
  }): Promise<MastraDBMessage[]> {
    const { messages } = args;
    this.logger.debug('Updating messages', { count: messages.length });

    if (!messages.length) {
      return [];
    }
    const check = this.#runFenceCheck(args.fence, 'updateMessages');
    const guard = runFenceGuard(check);
    const guardSql = guard ? ` AND ${guard.sql}` : '';
    const guardParams = guard?.params ?? [];

    const messageIds = messages.map(m => m.id);
    const fullTableName = this.#db.getTableName(TABLE_MESSAGES);
    const threadsTableName = this.#db.getTableName(TABLE_THREADS);

    try {
      // Get existing messages
      const placeholders = messageIds.map(() => '?').join(',');
      const selectQuery = `SELECT id, content, role, type, createdAt, thread_id AS threadId, resourceId FROM ${fullTableName} WHERE id IN (${placeholders})`;
      const existingMessages = (await this.#db.executeQuery({ sql: selectQuery, params: messageIds })) as any[];

      if (existingMessages.length === 0) {
        return [];
      }

      // Parse content from string to object for merging
      const parsedExistingMessages = existingMessages.map(msg => {
        if (typeof msg.content === 'string') {
          try {
            msg.content = JSON.parse(msg.content);
          } catch {
            // ignore if not valid json
          }
        }
        return msg;
      });

      const threadIdsToUpdate = new Set<string>();
      const updateQueries: { sql: string; params: any[] }[] = [];

      for (const existingMessage of parsedExistingMessages) {
        const updatePayload = messages.find(m => m.id === existingMessage.id);
        if (!updatePayload) continue;

        const { id, ...fieldsToUpdate } = updatePayload;
        if (Object.keys(fieldsToUpdate).length === 0) continue;

        threadIdsToUpdate.add(existingMessage.threadId!);
        if (
          'threadId' in updatePayload &&
          updatePayload.threadId &&
          updatePayload.threadId !== existingMessage.threadId
        ) {
          threadIdsToUpdate.add(updatePayload.threadId as string);
        }

        const setClauses: string[] = [];
        const values: any[] = [];

        const updatableFields = { ...fieldsToUpdate };

        // Special handling for content: merge in code, then update the whole field
        if (updatableFields.content) {
          const existingContent = existingMessage.content || {};
          const newContent = {
            ...existingContent,
            ...updatableFields.content,
            // Deep merge metadata if it exists on both
            ...(existingContent?.metadata && updatableFields.content.metadata
              ? {
                  metadata: {
                    ...existingContent.metadata,
                    ...updatableFields.content.metadata,
                  },
                }
              : {}),
          };
          setClauses.push(`content = ?`);
          values.push(JSON.stringify(newContent));
          delete updatableFields.content;
        }

        // Handle other fields
        for (const key in updatableFields) {
          if (Object.prototype.hasOwnProperty.call(updatableFields, key)) {
            const dbColumn = key === 'threadId' ? 'thread_id' : key;
            setClauses.push(`${dbColumn} = ?`);
            values.push(updatableFields[key as keyof typeof updatableFields]);
          }
        }

        if (setClauses.length > 0) {
          values.push(id, ...guardParams);
          const updateQuery = `UPDATE ${fullTableName} SET ${setClauses.join(', ')} WHERE id = ?${guardSql}`;
          updateQueries.push({ sql: updateQuery, params: values });
        }
      }

      // Execute all updates
      for (const query of updateQueries) {
        await executeFenced(this.#db, check, query);
      }

      // Update thread timestamps
      if (threadIdsToUpdate.size > 0) {
        const threadPlaceholders = Array.from(threadIdsToUpdate)
          .map(() => '?')
          .join(',');
        const threadUpdateQuery = `UPDATE ${threadsTableName} SET updatedAt = ? WHERE id IN (${threadPlaceholders})${guardSql}`;
        const threadUpdateParams = [new Date().toISOString(), ...Array.from(threadIdsToUpdate), ...guardParams];
        await executeFenced(this.#db, check, { sql: threadUpdateQuery, params: threadUpdateParams });
      }

      // Re-fetch updated messages
      const updatedMessages = (await this.#db.executeQuery({ sql: selectQuery, params: messageIds })) as any[];

      // Parse content back to objects
      return updatedMessages.map(message => {
        if (typeof message.content === 'string') {
          try {
            message.content = JSON.parse(message.content);
          } catch {
            // ignore if not valid json
          }
        }
        return message;
      });
    } catch (error) {
      if (isRunFenceConflictError(error)) throw error;
      throw new MastraError(
        {
          id: createStorageErrorId('CLOUDFLARE_D1', 'UPDATE_MESSAGES', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: { count: messages.length },
        },
        error,
      );
    }
  }

  async deleteMessages(messageIds: string[], options?: { fence?: RunFence }): Promise<void> {
    if (messageIds.length === 0) return;

    const fullTableName = this.#db.getTableName(TABLE_MESSAGES);
    const threadsTableName = this.#db.getTableName(TABLE_THREADS);
    const check = this.#runFenceCheck(options?.fence, 'deleteMessages');
    const guard = runFenceGuard(check);

    try {
      // Get unique thread IDs from messages before deleting
      const placeholders = messageIds.map(() => '?').join(',');
      const selectQuery = `SELECT DISTINCT thread_id FROM ${fullTableName} WHERE id IN (${placeholders})`;
      const threadResults = (await this.#db.executeQuery({ sql: selectQuery, params: messageIds })) as any[];
      const threadIds = threadResults.map((r: any) => r.thread_id).filter(Boolean);

      // Delete the messages
      const deleteQuery = createSqlBuilder()
        .delete(fullTableName)
        .where(`id IN (${placeholders})`, ...messageIds);
      if (guard) deleteQuery.andWhere(guard.sql, ...guard.params);
      await executeFenced(this.#db, check, deleteQuery.build());

      // Update thread timestamps for affected threads
      if (threadIds.length > 0) {
        const threadPlaceholders = threadIds.map(() => '?').join(',');
        const threadUpdateQuery = `UPDATE ${threadsTableName} SET updatedAt = ? WHERE id IN (${threadPlaceholders})${guard ? ` AND ${guard.sql}` : ''}`;
        const threadUpdateParams = [new Date().toISOString(), ...threadIds, ...(guard?.params ?? [])];
        await executeFenced(this.#db, check, { sql: threadUpdateQuery, params: threadUpdateParams });
      }
    } catch (error) {
      if (isRunFenceConflictError(error)) throw error;
      throw new MastraError(
        {
          id: createStorageErrorId('CLOUDFLARE_D1', 'DELETE_MESSAGES', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: { messageIds: JSON.stringify(messageIds) },
        },
        error,
      );
    }
  }
}
